import { A11yModule } from '@angular/cdk/a11y';
import { APP_INITIALIZER, NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SiLocalizationNgModule } from '@simpl/siemens-brand-localize-with-angular';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { TranslateModule } from '@ngx-translate/core';
import { SiAboutModule } from '@siemens/element-ng/about';
import { SiAccordionModule } from '@siemens/element-ng/accordion';
import { SiBreadcrumbModule } from '@siemens/element-ng/breadcrumb';
import { SiBreadcrumbRouterModule } from '@siemens/element-ng/breadcrumb-router';
import { SiCardModule } from '@siemens/element-ng/card';
import { SiCircleStatusModule } from '@siemens/element-ng/circle-status';
import { SiConnectionStrengthModule } from '@siemens/element-ng/connection-strength';
import { SiContentActionBarModule } from '@siemens/element-ng/content-action-bar';
import { SiCopyrightNoticeModule } from '@siemens/element-ng/copyright-notice';
import { SiDateRangeFilterModule } from '@siemens/element-ng/date-range-filter';
import { SiDatepickerModule } from '@siemens/element-ng/datepicker';
import { SiElectrontitlebarModule } from '@siemens/element-ng/electron-titlebar';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiFileUploaderModule } from '@siemens/element-ng/file-uploader';
import { SiFilterBarModule } from '@siemens/element-ng/filter-bar';
import { SiFilteredSearchModule } from '@siemens/element-ng/filtered-search';
import { SiFooterModule } from '@siemens/element-ng/footer';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiIconModule } from '@siemens/element-ng/icon';
import { SiIconStatusModule } from '@siemens/element-ng/icon-status';
import { SiInlineNotificationModule } from '@siemens/element-ng/inline-notification';
import { SiLandingPageModule } from '@siemens/element-ng/landing-page';
import { SiLanguageSwitcherModule } from '@siemens/element-ng/language-switcher';
import { SiLinkModule } from '@siemens/element-ng/link';
import { SiLoadingSpinnerModule } from '@siemens/element-ng/loading-spinner';
import { SiMainDetailContainerModule } from '@siemens/element-ng/main-detail-container';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiNavbarModule } from '@siemens/element-ng/navbar';
import { SiNavbarVerticalModule } from '@siemens/element-ng/navbar-vertical';
import { SiNumberInputModule } from '@siemens/element-ng/number-input';
import { SiPaginationModule } from '@siemens/element-ng/pagination';
import { SiPasswordStrengthModule } from '@siemens/element-ng/password-strength';
import { SiPasswordToggleModule } from '@siemens/element-ng/password-toggle';
import { SiPillsInputModule } from '@siemens/element-ng/pills-input';
import { SiPopoverLegacyModule } from '@siemens/element-ng/popover-legacy';
import { SiProgressbarModule } from '@siemens/element-ng/progressbar';
import { SiResizeObserverModule } from '@siemens/element-ng/resize-observer';
import { SiResultDetailsListModule } from '@siemens/element-ng/result-details-list';
import { SiSearchBarModule } from '@siemens/element-ng/search-bar';
import { SiSelectModule } from '@siemens/element-ng/select';
import { SiSidePanelModule } from '@siemens/element-ng/side-panel';
import { SiSliderModule } from '@siemens/element-ng/slider';
import { SiSortBarModule } from '@siemens/element-ng/sort-bar';
import { SiSplitModule } from '@siemens/element-ng/split';
import { SiStatusBarModule } from '@siemens/element-ng/status-bar';
import { SiTabsLegacyModule } from '@siemens/element-ng/tabs-legacy';
import { SiThresholdModule } from '@siemens/element-ng/threshold';
import { SiTooltipModule } from '@siemens/element-ng/tooltip';
import { SiTreeViewModule } from '@siemens/element-ng/tree-view';
import { SiTypeaheadModule } from '@siemens/element-ng/typeahead';
import { SiWizardModule } from '@siemens/element-ng/wizard';
import { SiDatatableModule } from '@siemens/element-ng/datatable';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { SiFooterModule as SiBrandFooterModule } from '@simpl/siemens-brand-ng/footer';
import { SI_VALIDATION_ERROR_RESOLVER_MAP } from '@simpl/siemens-brand-ng/form-group';
import { SiHeaderModule } from '@simpl/siemens-brand-ng/header';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HeaderComponent } from './header/header.component';
import { LogoutComponent } from './pages/logout/logout.component';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
} from '@azure/msal-browser';
import {
  MsalInterceptor, // Re-enabled for Authorization header
  MsalGuard,
  MsalBroadcastService,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MSAL_INTERCEPTOR_CONFIG,
  ProtectedResourceScopes,
} from '@azure/msal-angular';

import { msalConfig, protectedResources } from './auth-config';
import { ConfigService } from './services/config.service';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { ErrorInterceptor } from './services/error.interceptor';
import { SecureAuthInterceptor } from './services/secure-auth.interceptor';
import { UssDashboardLandingComponent } from './pages/uss-dashboard-landing/uss-dashboard-landing.component';
import { ProjectSidePanelComponent } from './pages/project-side-panel/project-side-panel.component';
import { CreateProjectComponent } from './pages/create-project/create-project.component';
import { ProjectsManagementComponent } from './pages/projects-management/projects-management.component';
import { AskAiDialogComponent } from './pages/ask-ai-dialog/ask-ai-dialog.component';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function initializeMsal(
  msalInstance: IPublicClientApplication
): () => Promise<void> {
  return () => msalInstance.initialize();
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<
    string,
    Array<string | ProtectedResourceScopes> | null
  >();

  protectedResourceMap.set(protectedResources.apiUsers.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiUsers.scopes.all],
    },
    {
      httpMethod: 'POST',
      scopes: [...protectedResources.apiUsers.scopes.all],
    },
    {
      httpMethod: 'PUT',
      scopes: [...protectedResources.apiUsers.scopes.all],
    },
    {
      httpMethod: 'DELETE',
      scopes: [...protectedResources.apiUsers.scopes.all],
    },
  ]);

  protectedResourceMap.set(protectedResources.apiProjects.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiProjects.scopes.all],
    },
    {
      httpMethod: 'POST',
      scopes: [...protectedResources.apiProjects.scopes.all],
    },
    {
      httpMethod: 'PUT',
      scopes: [...protectedResources.apiProjects.scopes.all],
    },
    {
      httpMethod: 'DELETE',
      scopes: [...protectedResources.apiProjects.scopes.all],
    },
  ]);

  protectedResourceMap.set(protectedResources.apiReaderProjects.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiReaderProjects.scopes.all],
    },
  ]);

  protectedResourceMap.set(protectedResources.apiCloudAccounts.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiCloudAccounts.scopes.all],
    },
  ]);

  protectedResourceMap.set(protectedResources.apiClusters.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiClusters.scopes.all],
    },
  ]);

  protectedResourceMap.set(protectedResources.apiAskAi.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiAskAi.scopes.all],
    },
    {
      httpMethod: 'POST',
      scopes: [...protectedResources.apiAskAi.scopes.all],
    },
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      // Request your API scope explicitly; consent happens once, avoids late interceptor-triggered redirect
      scopes: [...protectedResources.apiProjects.scopes.all],
      // Specify prompt to prevent SSO silent authentication issues with multiple accounts
      prompt: 'select_account',
    },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LogoutComponent,
    UssDashboardLandingComponent,
    CreateProjectComponent,
    ProjectSidePanelComponent,
    ProjectsManagementComponent,
    AskAiDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    A11yModule,
    AppRoutingModule,
    SiHeaderModule,
    SiFooterModule,
    SiBrandFooterModule,
    SiLoadingSpinnerModule,
    SiLocalizationNgModule,
    MsalModule,
    SiMenuModule,
    HttpClientModule,
    SiSearchBarModule,
    SiContentActionBarModule,
    SiDatatableModule,
    NgxDatatableModule,
    FormsModule,
    ReactiveFormsModule,
    SiFormModule,
    SiSliderModule,
    SiPaginationModule,
    SiEmptyStateModule,
    SiSelectModule,
    SiNavbarModule,
    SiLinkModule,
    SiAccordionModule,
    SiSidePanelModule,
    TranslateModule.forRoot(),
    SiAboutModule,
    SiBreadcrumbModule,
    SiBreadcrumbRouterModule,
    SiCardModule,
    SiCircleStatusModule,
    SiConnectionStrengthModule,
    SiCopyrightNoticeModule,
    SiDatepickerModule,
    SiDateRangeFilterModule,
    SiElectrontitlebarModule,
    SiFileUploaderModule,
    SiFilterBarModule,
    SiFilteredSearchModule,
    SiIconModule,
    SiIconStatusModule,
    SiInlineNotificationModule,
    SiLandingPageModule,
    SiLanguageSwitcherModule,
    SiMainDetailContainerModule,
    SiNavbarVerticalModule,
    SiNumberInputModule,
    SiPasswordStrengthModule,
    SiPasswordToggleModule,
    SiPillsInputModule,
    SiPopoverLegacyModule,
    SiProgressbarModule,
    SiResizeObserverModule,
    SiResultDetailsListModule,
    SiSortBarModule,
    SiSplitModule,
    SiStatusBarModule,
    SiTabsLegacyModule,
    SiThresholdModule,
    SiTooltipModule,
    SiTreeViewModule,
    SiTypeaheadModule,
    SiWizardModule
],
  providers: [
    {
      provide: SI_VALIDATION_ERROR_RESOLVER_MAP,
      useValue: { required: 'Value is required' },
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SecureAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    ConfigService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  // Only bootstrap AppComponent. MsalRedirectComponent will be placed in template to avoid double init.
  bootstrap: [AppComponent],
  // Allow custom element selectors from Siemens Simpl libraries without Angular detecting them as unknown
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
